(function () {
  'use strict';

  const normalizeHelpers = window.metaboxMwNormalize || {};
  const resolver = window.metaboxMwResolver || {};
  const editHydration = window.metaboxMwEditHydration || {};
  const METABOX_PERFORMANCE_STATS_REQUEST_EVENT = 'metabox:performanceStatsRequested';

  const asString = normalizeHelpers.asString || ((value) => String(value || '').trim());
  const normalizeOptionName =
    normalizeHelpers.normalizeOptionName || ((value) => String(value || '').trim().toLowerCase());
  const normalizeEntityName = normalizeHelpers.normalizeEntityName || normalizeOptionName;
  const normalizeMaterialLookupName = normalizeHelpers.normalizeMaterialLookupName || normalizeEntityName;
  const getMwValueLabel = normalizeHelpers.getMwValueLabel || (() => '');
  const getMaterialLabel = normalizeHelpers.getMaterialLabel || (() => '');

  const findMatchingSlot = resolver.findMatchingSlot || (() => undefined);
  const filterSlotsByActiveComponents = resolver.filterSlotsByActiveComponents || ((slots) => slots);
  const getActiveComponentContext = resolver.getActiveComponentContext || (() => ({ activeIds: new Set(), rootId: null }));
  const getConfiguratorComponents = resolver.getConfiguratorComponents || (() => []);
  const getRootComponents = resolver.getRootComponents || (() => []);
  const getActiveRootComponent = resolver.getActiveRootComponent || (() => null);
  const getPreferredComponentName = resolver.getPreferredComponentName || (() => '');
  const getRootVariantDescriptor = resolver.getRootVariantDescriptor || (() => null);
  const resolveComponentVariantForComponent = resolver.resolveComponentVariantForComponent || ((component) => component);
  const findComponentByName = resolver.findComponentByName || (() => null);
  const findSlotByName = resolver.findSlotByName || (() => null);
  const findSlotByNameFromSlots = resolver.findSlotByNameFromSlots || (() => null);
  const findMaterialByName = resolver.findMaterialByName || (() => null);

  function getMetaboxPerformanceStats() {
    const detail = { stats: null };
    window.dispatchEvent(
      new CustomEvent(METABOX_PERFORMANCE_STATS_REQUEST_EVENT, {
        detail,
      })
    );
    return detail.stats;
  }

  function parseMetaboxMwConfig() {
    const configElement = document.querySelector('[data-metabox-mw-config]');
    if (!configElement) return {};

    try {
      return JSON.parse(configElement.textContent || '{}');
    } catch (error) {
      console.error('[Metabox-MW] Failed to parse adapter config', error);
      return {};
    }
  }

  const metaboxMwConfig = parseMetaboxMwConfig();
  const CHECK_INTERVAL_MS = Number(metaboxMwConfig.checkIntervalMs) > 0 ? Number(metaboxMwConfig.checkIntervalMs) : 500;
  const MW_METABOX_MAPPING_ROWS = Array.isArray(metaboxMwConfig.mappingRows) ? metaboxMwConfig.mappingRows : [];
  const STEREO_BOX_POSITION_OPTION_NORM = normalizeOptionName('Stereo Box Position');
  const PLATFORM_MATERIAL_OPTION_LABEL = 'IsoAcoustics Turntable Isolation Platform Material';
  const PLATFORM_TOGGLE_ON_CURRENT_ROOT_ACTION = '@toggle_platform_on_current_root';

  let latestSlots = [];
  let connectIntervalId = null;
  let mappedActionQueue = Promise.resolve();
  const warnedAutoMapMisses = new Set();
  const mappingRules = parseMappingRows(MW_METABOX_MAPPING_ROWS);
  const mappingRulesByOption = buildMappingRulesByOption(mappingRules);

  function initMetaboxMwAdapter(event) {
    const slots = event.detail?.slots;
    if (!Array.isArray(slots) || slots.length === 0) {
      console.error('[Metabox-MW] No slots in event');
      return;
    }

    latestSlots = slots;
    ensureConnectLoop();
  }

  function ensureConnectLoop() {
    if (connectMwApoToMetabox(latestSlots)) {
      stopConnectLoop();
      return;
    }

    if (connectIntervalId) return;

    connectIntervalId = setInterval(() => {
      if (connectMwApoToMetabox(latestSlots)) {
        stopConnectLoop();
      }
    }, CHECK_INTERVAL_MS);
  }

  function stopConnectLoop() {
    if (!connectIntervalId) return;
    clearInterval(connectIntervalId);
    connectIntervalId = null;
  }

  function parseMappingRows(rows) {
    if (!Array.isArray(rows) || rows.length === 0) return [];

    const parsedRows = [];
    rows.forEach((row, rowIndex) => {
      const optionLabelRaw = asString(row && row.optionLabel);
      const optionIndexRaw = row && row.optionIndex;
      const optionIndex = normalizeOptionIndex(optionIndexRaw);
      const optionIndexIsWildcard = optionIndex === '*';
      const componentNameRaw = asString(row && row.componentName);
      const componentAction = normalizeComponentAction(componentNameRaw);
      const slotNameRaw = asString(row && row.slotName);
      const materialNameRaw = asString(row && row.materialName);
      const whenSelectedRaw = row && row.whenSelected;
      const whenSelected = normalizeWhenSelected(whenSelectedRaw);
      const slotDefined = isValueDefined(slotNameRaw);
      const materialDefined = isValueDefined(materialNameRaw);
      const hasComponentName = Boolean(componentNameRaw);
      const allowsSlotWithoutMaterial = componentAction === '@apply_to_all_slots' && slotDefined && !materialDefined;

      if (
        !optionLabelRaw ||
        (!optionIndexIsWildcard && (!Number.isInteger(optionIndex) || optionIndex < 1)) ||
        (!hasComponentName && !(slotDefined && materialDefined))
      ) {
        console.warn('[Metabox-MW] Invalid mapping row skipped', {
          rowIndex,
          optionLabelRaw,
          optionIndexRaw,
          componentNameRaw,
          row,
        });
        return;
      }

      if (slotDefined !== materialDefined && !allowsSlotWithoutMaterial) {
        console.warn('[Metabox-MW] Invalid mapping row: slot/material must be set together', {
          rowIndex,
          optionLabelRaw,
          optionIndex,
          componentNameRaw,
          slotNameRaw,
          materialNameRaw,
          row,
        });
        return;
      }
      if (isValuePresent(whenSelectedRaw) && !isBooleanLikeValue(whenSelectedRaw)) {
        console.warn('[Metabox-MW] Invalid when_selected value, defaulting to true', {
          rowIndex,
          whenSelectedRaw,
        });
      }

      parsedRows.push({
        optionLabelRaw,
        optionLabelNorm: normalizeOptionName(optionLabelRaw),
        optionIndex,
        optionIndexIsWildcard,
        componentNameRaw,
        componentNameNorm: componentAction ? '' : normalizeEntityName(componentNameRaw),
        componentAction,
        slotNameRaw,
        slotNameNorm: slotDefined ? normalizeEntityName(slotNameRaw) : '',
        materialNameRaw,
        materialNameNorm: materialDefined ? normalizeEntityName(materialNameRaw) : '',
        setMaterial: slotDefined && materialDefined,
        whenSelected,
      });
    });

    return parsedRows;
  }

  function normalizeOptionIndex(value) {
    const textValue = asString(value);
    if (!textValue) return '*';
    if (textValue === '*') return '*';

    const numericValue = Number(value);
    if (Number.isInteger(numericValue)) return numericValue;

    return null;
  }

  function buildMappingRulesByOption(rules) {
    const map = new Map();
    rules.forEach((rule) => {
      if (!rule.optionLabelNorm) return;
      if (!map.has(rule.optionLabelNorm)) {
        map.set(rule.optionLabelNorm, []);
      }
      map.get(rule.optionLabelNorm).push(rule);
    });
    return map;
  }

  function getRulesForOption(optionLabel) {
    const optionLabelNorm = normalizeOptionName(optionLabel);
    if (!optionLabelNorm || !mappingRulesByOption.has(optionLabelNorm)) return [];
    return mappingRulesByOption.get(optionLabelNorm);
  }

  function isValueDefined(value) {
    if (!value) return false;
    return value !== '-';
  }

  function normalizeWhenSelected(value) {
    if (typeof value === 'boolean') return value;

    const textValue = asString(value).toLowerCase();
    if (['true', '1', 'yes'].includes(textValue)) return true;
    if (['false', '0', 'no'].includes(textValue)) return false;

    return true;
  }

  function isBooleanLikeValue(value) {
    if (typeof value === 'boolean') return true;
    const textValue = asString(value).toLowerCase();
    return ['true', 'false', '1', '0', 'yes', 'no'].includes(textValue);
  }

  function normalizeComponentAction(value) {
    const normalizedValue = asString(value).toLowerCase();
    if (!normalizedValue) return '';

    const customActions = getCustomActionsRegistry();
    if (!customActions || typeof customActions.normalizeAction !== 'function') return '';

    return customActions.normalizeAction(normalizedValue) || '';
  }

  function isValuePresent(value) {
    if (value === null || value === undefined) return false;
    if (typeof value === 'string') return value.trim() !== '';
    return true;
  }

  function shouldRunRuleOnInputState(rule, isChecked) {
    if (!rule) return false;
    return rule.whenSelected ? isChecked : !isChecked;
  }

  function connectMwApoToMetabox(slots) {
    if (!Array.isArray(slots) || slots.length === 0) return false;

    const mwApoContainer = document.querySelector('.mw-apo-container');
    if (!mwApoContainer) {
      return false;
    }

    const mwOptions = mwApoContainer.querySelectorAll('.mw-option');
    if (mwOptions.length === 0) {
      return false;
    }

    connectScrollToConfigurator(mwApoContainer);

    mwOptions.forEach((mwOption) => {
      const labelElement = mwOption.querySelector('.mw-text.mw-text--style-strong');
      if (!labelElement) {
        console.error('[Metabox-MW] No label element found');
        return;
      }

      const mwOptionName = labelElement.textContent.trim();
      const hasMappedRules = connectMappedValues(mwOption, mwOptionName);
      const matchingSlot = findMatchingSlot(slots, mwOptionName);

      if (matchingSlot) {
        connectValues(mwOption, matchingSlot, mwOptionName, { suppressAutoMapWarnings: hasMappedRules });
      } else if (!hasMappedRules) {
        console.warn(`[Metabox-MW] No matching slot for "${mwOptionName}"`);
      }
    });

    if (typeof editHydration.maybeHydrateEditSessionSelections === 'function') {
      editHydration.maybeHydrateEditSessionSelections(mwApoContainer, {
        getRulesForOption,
        getLatestSlots: () => latestSlots,
        getMappedActionQueue: () => mappedActionQueue,
      });
    }

    return true;
  }

  function getMwOptionLabel(mwOption) {
    return mwOption.querySelector('.mw-text.mw-text--style-strong')?.textContent?.trim() || '';
  }

  function getActiveComponents() {
    const activeContext = getActiveComponentContext();
    return getConfiguratorComponents().filter((component) => activeContext.activeIds.has(component && component.id));
  }

  function isPlatformEnabledComponent(component) {
    const componentNameNorm = normalizeEntityName(getPreferredComponentName(component));
    if (!componentNameNorm) return false;
    if (componentNameNorm.includes('noplatform') || componentNameNorm.includes('no platform')) return false;
    return componentNameNorm.includes('platform');
  }

  function shouldPreservePlatformStateForRootSwitch(rule, context, component) {
    if (!rule || !component?.componentType?.isRoot) return false;

    const optionLabelNorm = normalizeOptionName(context?.mwOptionName || rule.optionLabelRaw);
    return optionLabelNorm === STEREO_BOX_POSITION_OPTION_NORM;
  }

  function getCustomActionHelpers() {
    return {
      asString,
      normalizeOptionName,
      normalizeEntityName,
      normalizeMaterialLookupName,
      getMwValueLabel,
      getMaterialLabel,
      getBaseModel: () => window.metaboxConfigurator?.baseModel || '',
      getConfiguratorComponents,
      getPreferredComponentName,
      getRootVariantDescriptor,
      getRootComponents,
      getActiveComponents: () => {
        const activeContext = getActiveComponentContext();
        return getConfiguratorComponents().filter((component) => activeContext.activeIds.has(component && component.id));
      },
      getActiveRootComponent,
      filterSlotsByActiveComponents,
      findMatchingSlot,
      findSlotByNameFromSlots,
      findMaterialByName,
    };
  }

  function buildCustomActionPayload(rule, context) {
    return {
      rule,
      context,
      latestSlots,
      helpers: getCustomActionHelpers(),
    };
  }

  function createCustomActionContext(rule, context) {
    const customActions = getCustomActionsRegistry();
    if (!customActions || typeof customActions.createContext !== 'function') {
      return null;
    }

    return customActions.createContext(buildCustomActionPayload(rule, context));
  }

  function snapshotPlatformStateForRootSwitch(context) {
    const actionContext = createCustomActionContext(
      {
        componentAction: PLATFORM_TOGGLE_ON_CURRENT_ROOT_ACTION,
      },
      context
    );
    const activeRoot = getActiveRootComponent();
    const activeRootDescriptor = activeRoot ? getRootVariantDescriptor(activeRoot) : null;
    const activePlatformComponent =
      getActiveComponents().find((component) => !component?.componentType?.isRoot && isPlatformEnabledComponent(component)) || null;
    const platformEnabled = Boolean(activeRootDescriptor?.platformEnabled || activePlatformComponent);
    const materialLabel = platformEnabled && actionContext ? actionContext.getSelectedOptionLabel(PLATFORM_MATERIAL_OPTION_LABEL) : '';

    return {
      platformEnabled,
      materialLabel,
    };
  }

  async function selectMappedComponent(component, context, options = {}) {
    if (!component) return false;

    const canSelectComponent = Boolean(window.metaboxConfigurator && window.metaboxConfigurator.selectComponent);
    if (!canSelectComponent) {
      console.error('[Metabox-MW] selectComponent API not available');
      return false;
    }

    const componentTypeId = component?.componentType?.id || null;
    const isRoot = Boolean(component?.componentType?.isRoot);

    const componentSelected = window.metaboxConfigurator.selectComponent(component.id, componentTypeId, isRoot);
    if (!componentSelected) {
      console.warn('[Metabox-MW] Failed to select component', {
        componentId: component.id,
        componentName: component.title || component.name,
        context,
      });
      return false;
    }

    await waitForSlotsRefresh();
    return true;
  }

  async function restorePlatformStateAfterRootSwitch(snapshot, context) {
    if (!snapshot?.platformEnabled) return true;

    const toggleResult = await executeCustomMappedAction(
      {
        componentAction: PLATFORM_TOGGLE_ON_CURRENT_ROOT_ACTION,
        whenSelected: true,
      },
      {
        ...context,
        source: 'root-switch-platform-restore',
      }
    );

    if (toggleResult?.handled && toggleResult.success === false && !toggleResult.component) {
      console.warn('[Metabox-MW] Root switch platform restore failed: unable to resolve enabled platform target', {
        context,
      });
      return false;
    }

    if (toggleResult?.component) {
      const activeContext = getActiveComponentContext();
      if (!activeContext.activeIds.has(toggleResult.component.id)) {
        const componentSelected = await selectMappedComponent(toggleResult.component, {
          ...context,
          source: 'root-switch-platform-restore',
        });
        if (!componentSelected) {
          return false;
        }
      }
    }

    if (!snapshot.materialLabel) {
      return true;
    }

    const actionContext = createCustomActionContext(
      {
        componentAction: PLATFORM_TOGGLE_ON_CURRENT_ROOT_ACTION,
      },
      {
        ...context,
        source: 'root-switch-platform-restore',
      }
    );
    if (!actionContext) {
      console.warn('[Metabox-MW] Root switch platform restore failed: custom action context unavailable', {
        materialLabel: snapshot.materialLabel,
        context,
      });
      return false;
    }

    const materialChanged = actionContext.applyMaterialToActiveOption(PLATFORM_MATERIAL_OPTION_LABEL, snapshot.materialLabel);
    return materialChanged !== false;
  }

  function connectValues(mwOption, matchingSlot, mwOptionName, options) {
    const suppressAutoMapWarnings = Boolean(options && options.suppressAutoMapWarnings);
    const mwValues = Array.from(mwOption.querySelectorAll('.mw-option__value'));
    const metaboxMaterials = Array.isArray(matchingSlot.enabledMaterials) ? matchingSlot.enabledMaterials : [];

    if (!mwValues.length || !metaboxMaterials.length) return;

    const metaboxMaterialsByTitle = new Map();
    metaboxMaterials.forEach((material, index) => {
      const materialTitle = getMaterialLabel(material, index);
      const normalizedTitle = normalizeMaterialLookupName(materialTitle);
      if (!normalizedTitle || metaboxMaterialsByTitle.has(normalizedTitle)) return;
      metaboxMaterialsByTitle.set(normalizedTitle, material);
    });

    mwValues.forEach((mwValue, index) => {
      const input = mwValue.querySelector('input.mw-option__control');
      if (!input) {
        console.error('[Metabox-MW] Option value input not found');
        return;
      }

      if (input.__metaboxMwChangeHandler) {
        input.removeEventListener('change', input.__metaboxMwChangeHandler);
      }

      const slotId = matchingSlot.id;
      const mwValueLabel = getMwValueLabel(mwValue, index);
      const normalizedMwValueLabel = normalizeMaterialLookupName(mwValueLabel);
      const material = normalizedMwValueLabel ? metaboxMaterialsByTitle.get(normalizedMwValueLabel) : null;
      const materialId = material && material.id;

      if (!materialId && !suppressAutoMapWarnings) {
        const warningKey = [mwOptionName, mwValueLabel, matchingSlot.id || '', matchingSlot.label || ''].join('::');
        if (warnedAutoMapMisses.has(warningKey)) return;
        warnedAutoMapMisses.add(warningKey);
        console.warn('[Metabox-MW] Unable to auto-map MW value to Metabox material by title', {
          mwOptionName,
          mwValueLabel,
          normalizedMwValueLabel,
          matchedSlotLabel: matchingSlot.label || null,
          matchedSlotId: matchingSlot.id || null,
          availableMetaboxMaterials: metaboxMaterials.map((candidate, candidateIndex) => ({
            title: getMaterialLabel(candidate, candidateIndex),
            normalizedTitle: normalizeMaterialLookupName(getMaterialLabel(candidate, candidateIndex)),
            id: candidate && candidate.id ? candidate.id : null,
          })),
        });
      }

      if (!slotId || !materialId) return;

      const changeHandler = () => {
        if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
        if (window.metaboxConfigurator && window.metaboxConfigurator.changeMaterial) {
          window.metaboxConfigurator.changeMaterial(slotId, materialId);
        } else {
          console.error('[Metabox-MW] Metabox API not available');
        }
      };

      input.__metaboxMwChangeHandler = changeHandler;
      input.addEventListener('change', changeHandler);
    });
  }

  function connectMappedValues(mwOption, mwOptionName) {
    const rulesForOption = getRulesForOption(mwOptionName);
    if (!rulesForOption.length) {
      return false;
    }

    const mwValues = Array.from(mwOption.querySelectorAll('.mw-option__value'));
    if (!mwValues.length) return true;

    mwValues.forEach((mwValue, index) => {
      const input = mwValue.querySelector('input.mw-option__control');
      if (!input) {
        console.error('[Metabox-MW] Option value input not found');
        return;
      }

      if (input.__metaboxMwChangeHandler) {
        input.removeEventListener('change', input.__metaboxMwChangeHandler);
      }
      if (input.__metaboxMwRuleHandler) {
        input.removeEventListener('change', input.__metaboxMwRuleHandler);
      }
      if (input.__metaboxMwClickHandler) {
        input.removeEventListener('click', input.__metaboxMwClickHandler);
      }
      if (mwValue.__metaboxMwPointerDownHandler) {
        mwValue.removeEventListener('pointerdown', mwValue.__metaboxMwPointerDownHandler, true);
      }

      const optionIndex = index + 1;
      const matchingRules = rulesForOption.filter((rule) => rule.optionIndexIsWildcard || rule.optionIndex === optionIndex);

      const runMappedRulesForState = (isChecked, triggerState, source) => {
        if (!matchingRules.length) {
          console.warn('[Metabox-MW] No mapping row for option index', { mwOptionName, optionIndex, source });
          return;
        }

        const applicableRules = matchingRules.filter((rule) => shouldRunRuleOnInputState(rule, isChecked));
        if (!applicableRules.length) return;

        applicableRules.forEach((rule, ruleIndex) => {
          queueMappedRuleExecution(rule, {
            mwOptionName,
            optionIndex,
            triggerState,
            source,
            ruleIndex,
            rulesCount: applicableRules.length,
          });
        });
      };

      const pointerDownHandler = () => {
        input.__metaboxMwCheckedBeforePointerDown = Boolean(input.checked);
      };

      const clickHandler = () => {
        const wasCheckedBeforePointerDown = Boolean(input.__metaboxMwCheckedBeforePointerDown);

        if (input.type === 'radio' && wasCheckedBeforePointerDown) {
          runMappedRulesForState(false, 'unchecked', 'click-reclick-selected-radio');
        }

        input.__metaboxMwCheckedBeforePointerDown = undefined;
      };

      const mappedHandler = () => {
        const hasCheckedState = input.type === 'radio' || input.type === 'checkbox';
        const isChecked = hasCheckedState ? Boolean(input.checked) : true;
        const triggerState = isChecked ? 'checked' : 'unchecked';
        runMappedRulesForState(isChecked, triggerState, 'change');
      };

      mwValue.__metaboxMwPointerDownHandler = pointerDownHandler;
      mwValue.addEventListener('pointerdown', pointerDownHandler, true);
      input.__metaboxMwClickHandler = clickHandler;
      input.addEventListener('click', clickHandler);
      input.__metaboxMwRuleHandler = mappedHandler;
      input.addEventListener('change', mappedHandler);
    });

    return true;
  }

  function queueMappedRuleExecution(rule, context) {
    mappedActionQueue = mappedActionQueue
      .then(() => executeMappedRule(rule, context))
      .catch((error) => {
        console.error('[Metabox-MW] Mapping rule execution failed', error);
      });
    return mappedActionQueue;
  }

  async function executeMappedRule(rule, context) {
    const hasComponentName = Boolean(rule.componentNameNorm);
    const hasComponentAction = Boolean(rule.componentAction);
    let component = null;

    if (hasComponentAction) {
      const customActionResult = await executeCustomMappedAction(rule, context);
      if (customActionResult && customActionResult.handled) {
        if (!customActionResult.component) {
          return customActionResult.success !== false;
        }
        component = customActionResult.component;
      }
    }

    if (hasComponentName || component) {
      if (!component) {
        component = findComponentByName(rule.componentNameNorm);
        component = resolveComponentVariantForComponent(component);
      }

      if (!component) {
        console.warn('[Metabox-MW] Mapping component target not found', {
          componentName: rule.componentNameRaw,
          componentAction: rule.componentAction || null,
          context,
        });
        return false;
      }

      const shouldPreservePlatform = shouldPreservePlatformStateForRootSwitch(rule, context, component);
      const platformStateSnapshot = shouldPreservePlatform ? snapshotPlatformStateForRootSwitch(context) : null;

      const componentSelected = await selectMappedComponent(component, context, {
        logRootSelection: Boolean(component?.componentType?.isRoot),
      });
      if (!componentSelected) {
        return false;
      }

      if (platformStateSnapshot) {
        const platformRestored = await restorePlatformStateAfterRootSwitch(platformStateSnapshot, context);
        if (!platformRestored) {
          return false;
        }
      }
    }

    if (!rule.setMaterial) return true;
    if (!(window.metaboxConfigurator && window.metaboxConfigurator.changeMaterial)) {
      console.error('[Metabox-MW] changeMaterial API not available');
      return false;
    }

    if (!hasComponentName) {
      const activeSlots = filterSlotsByActiveComponents(latestSlots);
      const slot =
        findSlotByNameFromSlots(activeSlots, rule.slotNameNorm) || findSlotByNameFromSlots(latestSlots, rule.slotNameNorm);

      if (!slot) {
        console.warn('[Metabox-MW] Mapping slot not found in active context', {
          slotName: rule.slotNameRaw,
          context,
        });
        return false;
      }

      const material = findMaterialByName(slot, rule.materialNameNorm);
      if (!material) {
        console.warn('[Metabox-MW] Mapping material not found in active context', {
          slotName: rule.slotNameRaw,
          materialName: rule.materialNameRaw,
          context,
        });
        return false;
      }

      return window.metaboxConfigurator.changeMaterial(slot.id, material.id);
    }

    const freshComponent = findComponentByName(rule.componentNameNorm) || component;
    const slot = findSlotByName(freshComponent, rule.slotNameNorm);
    if (!slot) {
      console.warn('[Metabox-MW] Mapping slot not found', {
        componentName: rule.componentNameRaw,
        slotName: rule.slotNameRaw,
        context,
      });
      return false;
    }

    const material = findMaterialByName(slot, rule.materialNameNorm);
    if (!material) {
      console.warn('[Metabox-MW] Mapping material not found', {
        componentName: rule.componentNameRaw,
        slotName: rule.slotNameRaw,
        materialName: rule.materialNameRaw,
        context,
      });
      return false;
    }

    return window.metaboxConfigurator.changeMaterial(slot.id, material.id, freshComponent.id);
  }

  function getCustomActionsRegistry() {
    const registry = window.metaboxMwCustomActions;
    if (!registry || typeof registry !== 'object') return null;
    return registry;
  }

  async function executeCustomMappedAction(rule, context) {
    if (!rule || !rule.componentAction) return null;

    const customActions = getCustomActionsRegistry();
    if (!customActions || typeof customActions.execute !== 'function') {
      console.warn('[Metabox-MW] Custom action registry not available', {
        componentAction: rule.componentAction,
        context,
      });
      return { handled: true, success: false };
    }

    const result = await customActions.execute(rule.componentAction, buildCustomActionPayload(rule, context));

    if (result && result.handled) return result;

    console.warn('[Metabox-MW] Unsupported custom action', {
      componentAction: rule.componentAction,
      context,
    });

    return { handled: true, success: false };
  }

  function waitForSlotsRefresh(timeoutMs = 1200) {
    return new Promise((resolve) => {
      let done = false;

      function cleanup() {
        if (done) return;
        done = true;
        window.removeEventListener('metabox:slotsReady', handleSlotsReady);
        clearTimeout(timeoutId);
      }

      function handleSlotsReady(event) {
        if (Array.isArray(event.detail?.slots)) {
          latestSlots = event.detail.slots;
        }
        cleanup();
        resolve(true);
      }

      const timeoutId = setTimeout(() => {
        cleanup();
        resolve(false);
      }, timeoutMs);

      window.addEventListener('metabox:slotsReady', handleSlotsReady, { once: true });
    });
  }

  function connectScrollToConfigurator(mwApoContainer) {
    if (!mwApoContainer) return;
    const mwValues = mwApoContainer.querySelectorAll('.mw-option__value');
    mwValues.forEach((mwValue) => {
      const input = mwValue.querySelector('input.mw-option__control');
      if (!input) {
        console.error('[Metabox-MW] Option value input not found');
        return;
      }
      if (input.__metaboxScrollHandler) {
        input.removeEventListener('change', input.__metaboxScrollHandler);
      }
      input.__metaboxScrollHandler = scrollToConfigurator;
      input.addEventListener('change', scrollToConfigurator);
    });
  }

  function scrollToConfigurator() {
    const configuratorMedia = document.querySelector('.product-gallery__media[data-media-id="configurator"]');
    if (!configuratorMedia) return;

    const configuratorThumbnail = document.querySelector(
      'button.product-gallery__thumbnail[data-media-id="configurator"]'
    );

    if (!configuratorThumbnail) return;

    configuratorThumbnail.click();
  }

  function reapplyCheckedSelections() {
    const mwApoContainer = document.querySelector('.mw-apo-container');
    if (!mwApoContainer) return Promise.resolve(false);

    const inputs = Array.from(mwApoContainer.querySelectorAll('input.mw-option__control'));
    inputs.forEach((input) => {
      if (!input) return;
      if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) return;
      if (!input.__metaboxMwChangeHandler && !input.__metaboxMwRuleHandler) return;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });

    return mappedActionQueue;
  }

  window.addEventListener('metabox:slotsReady', initMetaboxMwAdapter);

  if (window.metaboxConfigurator && Array.isArray(window.metaboxConfigurator.slots) && window.metaboxConfigurator.slots.length) {
    latestSlots = window.metaboxConfigurator.slots;
    ensureConnectLoop();
  }

  window.metaboxMwAdapterAuditApi = {
    getLatestSlots: () => latestSlots.slice(),
    getMappingRules: () => mappingRules.slice(),
    getRulesForOption: (optionLabel) => getRulesForOption(optionLabel).slice(),
    getMetaboxPerformanceStats,
  };

  window.metaboxMwAdapter = {
    reapplyCheckedSelections,
  };
})();
